import { Injectable, OnDestroy } from "@angular/core";
import { Observable, Subject, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { NovaRealtimeRelayService } from "./nova-realtime-relay.service";
import { NovaRealtimeSessionService } from "./nova-realtime-session.service";
import {
  NovaRealtimeRelayExchange,
  NovaRealtimeRelayedEventTypes,
  NovaRealtimeToolCallOutcome,
} from "./nova-realtime.model";

/**
 * What is sent back when the relay could not answer at all.
 *
 * THE ONLY OUTPUT THIS CLIENT EVER COMPOSES, and it deliberately answers nothing. Two of the
 * ticket's requirements pull against each other: the client must never compose a function output,
 * yet a call the relay cannot answer must still return something or the session hangs waiting for
 * ever. This is the resolution - it reports that the tool did not run, and says nothing about where
 * the user was taken. It cannot name a destination, so it cannot name a wrong one.
 */
const RELAY_UNAVAILABLE_OUTPUT = JSON.stringify({ ok: false, error: "unavailable" });

/**
 * FLIP-275. Answers the model's function calls with what the SERVER resolved.
 *
 * Under Realtime, `raise_safety_escalation` is a function call rather than a
 * `[[SAFETY_ESCALATION_TRIGGERED]]` text tag. That is not a refactor for its own sake: a Realtime
 * model speaks its own output, so a tag left in the response would be READ ALOUD.
 *
 * WHERE THIS SITS. FLIP-274's relay already posts function-call events and publishes what came
 * back on `responses$`; this service consumes that rather than relaying again. Two subscriptions
 * to `events$` both POSTing the same call would have the server answer it twice and the model
 * receive two outputs for one `callId`.
 *
 * WHY IT IS A SERVICE AND NOT PART OF THE CHAT COMPONENT, for the same reason FLIP-273's transport
 * is. A voice call outlives what started it - minimising to the FAB (FLIP-234) and in-app
 * navigation both keep the session running - and a round trip owned by NovaCompanionChatComponent
 * would be torn down with the template. A tool call whose output is never sent does not fail
 * loudly; it leaves the model blocked for the rest of the session, waiting on an answer that can
 * no longer come. Answering is mandatory and happens here; surfacing crisis resources is only
 * possible when something is on screen, so it is emitted separately for whoever is.
 *
 * VOICE OFFERS NO LINK. `navigate_to` gives a clickable button, which a spoken session has nowhere
 * to show, so it is a text-only tool and never reaches this service with a destination. A stale
 * session that still calls it is answered like any other call, verbatim from the server, which
 * refuses it - and nothing is rendered.
 */
@Injectable({
  providedIn: "root",
})
export class NovaRealtimeToolCallService implements OnDestroy {
  private readonly outcomeSource = new Subject<NovaRealtimeToolCallOutcome>();
  /**
   * What a completed tool call leaves for the UI.
   *
   * Not replayed: a subscriber arriving after the fact wants what happens next, and re-emitting a
   * historic escalation would re-raise it on every reopen - the same trap `pushStoredTurn` avoids
   * by stripping the safety tag without acting on it.
   */
  readonly outcome$: Observable<NovaRealtimeToolCallOutcome> = this.outcomeSource.asObservable();

  private readonly subscription: Subscription;

  constructor(
    private session: NovaRealtimeSessionService,
    private relay: NovaRealtimeRelayService
  ) {
    // Subscribed on construction rather than behind a start()/stop() pair the session lifecycle
    // would have to remember to call. The relay only publishes while a session is running, so
    // listening for the app's lifetime costs nothing between calls - and a tool call arriving
    // while nobody had remembered to start listening is a model blocked for the rest of the
    // session, which is precisely the failure this ticket exists to prevent.
    this.subscription = this.relay.responses$
      .pipe(
        filter(
          (exchange) =>
            exchange.request.type === NovaRealtimeRelayedEventTypes.FunctionCallArgumentsDone
        )
      )
      .subscribe((exchange) => this.handleExchange(exchange));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * One tool call: send back what the server said, then tell the UI.
   *
   * The `call_id` is the only part that cannot be recovered from anywhere else - without it the
   * output cannot be addressed to the call that is waiting - so an exchange arriving without one
   * is dropped rather than answered into the void.
   */
  private handleExchange(exchange: NovaRealtimeRelayExchange): void {
    const { request, response } = exchange;

    // Prefer the call_id the server echoed - it is the one it built the output against - but fall
    // back to the one the call was sent with, since an output addressed to nothing unblocks
    // nothing.
    const callId = response?.callId ?? request.callId;
    if (!callId) {
      return;
    }

    // `functionCallOutput` goes out VERBATIM. It arrives already serialised and is not re-parsed,
    // re-shaped or topped up here - the server resolved the title against the Learn catalogue, and
    // a client answering for itself could tell Nova it opened a lesson the catalogue has never
    // heard of. That is the FLIP-224 rule, and a correctness property rather than a formality.
    this.session.sendFunctionCallOutput(
      callId,
      response?.functionCallOutput ?? RELAY_UNAVAILABLE_OUTPUT
    );

    if (!response) {
      // The relay never answered. The model has been unblocked, but nothing was resolved, and a
      // toast offering a destination the server never validated is what this ticket forbids.
      return;
    }

    if (response.safetyEscalationTriggered) {
      this.outcomeSource.next({ safetyEscalationTriggered: true });
    }
  }
}
